import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DisplayLabel } from '../../../../../shared/models/displayLabel';
import { EntrepreneurLabel } from '../../../../../shared/models/enums/entrepreneur.enum';
import { RSJService } from '../../../../../shared/services/rsj.service';
import { RsjInstructionInsertionDto } from '../../dto/rsj-instruction-insertion.dto';
import { RsjInstructionSituationDto } from '../../dto/rsj-instruction-situation.dto';

@Component({
  selector: 'app-rsj-instruction-insertion',
  templateUrl: './rsj-instruction-insertion.component.html',
  standalone: false,
})
export class RsjInstructionInsertionComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public insertionForm: UntypedFormGroup;
  public updated: boolean = false;
  public submitted: boolean = false;

  public situations: DisplayLabel[] = [];
  public mobilizedPlans: DisplayLabel[] = [];

  public EntrepreneurLabel = EntrepreneurLabel;

  @Input() public rsjInsertion: RsjInstructionInsertionDto;
  @Input() public disabled: boolean;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly rsjService: RSJService,
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.initList();
    this.initForm();
    this.initValueChangeHandlers();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.insertionForm.valid;
  }

  public getDto(): RsjInstructionInsertionDto {
    if (this.insertionForm.valid) {
      this.rsjInsertion = {
        entrepreneur: this.insertionForm.controls.entrepreneur.value,
        rsjSituations: this.rsjSituations.value
          .map(
            (value: any) =>
              ({
                rsjSituationId: value.situationId,
                comment: value.situationComment,
              }) as RsjInstructionSituationDto,
          )
          .filter((situation: RsjInstructionSituationDto) => situation.rsjSituationId !== null),
        outOfEducationSystem: this.insertionForm.controls.outOfEducationSystem.value || false,
        socialCareInProgress: this.insertionForm.controls.socialCareInProgress.value,
        socialStructureName: this.insertionForm.controls.socialStructureName.value,
        aseService: this.insertionForm.controls.aseService.value,
        aseServiceLyon: this.insertionForm.controls.aseServiceLyon.value,
        rsjMobilizedPlanIds: this.rsjMobilizedPlans.value.filter((value: number) => value !== null),
        healthInsurance: this.insertionForm.controls.healthInsurance.value,
        complementaryHealth: this.insertionForm.controls.complementaryHealth.value,
        givenUpCareInPast6Months: this.insertionForm.controls.givenUpCareInPast6Months.value,
        individualInternetAccess: this.insertionForm.controls.individualInternetAccess.value,
        connectedToTCLNetwork: this.insertionForm.controls.connectedToTCLNetwork.value,
        leisureAccessInPast12Months: this.insertionForm.controls.leisureAccessInPast12Months.value,
      } as RsjInstructionInsertionDto;
    }
    return this.rsjInsertion;
  }

  public syncForm(syncInsertion: RsjInstructionInsertionDto): void {
    this.updated = true;
    this.insertionForm.controls.entrepreneur.setValue(syncInsertion.entrepreneur);
    this.insertionForm.controls.healthInsurance.setValue(syncInsertion.healthInsurance);
    this.insertionForm.updateValueAndValidity();
  }

  public get rsjSituations(): UntypedFormArray {
    return this.insertionForm.controls.rsjSituations as UntypedFormArray;
  }

  public addNewSituation(): void {
    this.rsjSituations.push(
      this.fb.group({
        situationId: [undefined, [Validators.required]],
        situationComment: [],
      }),
    );
  }

  public removeSituation(index: number): void {
    this.rsjSituations.removeAt(index);
  }

  public get rsjMobilizedPlans(): UntypedFormArray {
    return this.insertionForm.controls.rsjMobilizedPlans as UntypedFormArray;
  }

  public addNewMobilizedPlan(): void {
    this.rsjMobilizedPlans.push(new UntypedFormControl(undefined));
  }

  public removeMobilizedPlan(index: number): void {
    this.rsjMobilizedPlans.removeAt(index);
  }

  public filterSituations(index: number): DisplayLabel[] {
    return this.situations.filter(
      (item) =>
        item.id === this.rsjSituations.value[index].situationId ||
        !(this.rsjSituations.value?.map((value) => value.situationId) || []).includes(item.id),
    );
  }

  public filterMobilizedPlans(index: number): DisplayLabel[] {
    return this.mobilizedPlans.filter(
      (item) => item.id === this.rsjMobilizedPlans.value[index] || !(this.rsjMobilizedPlans.value || []).includes(item.id),
    );
  }

  private async initList(): Promise<void> {
    this.situations = await this.rsjService.findSituations();
    this.mobilizedPlans = await this.rsjService.findMobilizedPlans();
  }

  private initForm(): void {
    this.insertionForm = this.fb.group({
      entrepreneur: [{ value: this.rsjInsertion?.entrepreneur, disabled: true }],
      rsjSituations: this.fb.array(
        (this.rsjInsertion?.rsjSituations ?? [new RsjInstructionSituationDto()]).map((item) =>
          this.fb.group({
            situationId: [item.rsjSituationId, [Validators.required]],
            situationComment: [item.comment],
          }),
        ),
      ),
      outOfEducationSystem: [this.rsjInsertion?.outOfEducationSystem, Validators.requiredTrue],
      socialCareInProgress: [this.rsjInsertion?.socialCareInProgress, [Validators.required]],
      socialStructureName: [this.rsjInsertion?.socialStructureName],
      aseService: [this.rsjInsertion?.aseService, [Validators.required]],
      aseServiceLyon: [this.rsjInsertion?.aseServiceLyon],
      rsjMobilizedPlans: this.fb.array(
        (this.rsjInsertion?.rsjMobilizedPlanIds || [null]).map((item) => new UntypedFormControl(item, [Validators.required])),
      ),
      healthInsurance: [{ value: this.rsjInsertion?.healthInsurance, disabled: true }],
      complementaryHealth: [this.rsjInsertion?.complementaryHealth],
      givenUpCareInPast6Months: [this.rsjInsertion?.givenUpCareInPast6Months],
      individualInternetAccess: [this.rsjInsertion?.individualInternetAccess],
      connectedToTCLNetwork: [this.rsjInsertion?.connectedToTCLNetwork],
      leisureAccessInPast12Months: [this.rsjInsertion?.leisureAccessInPast12Months],
    });
    this.updateSocialStructureVisibility();
    this.updateAseServiceLyonVisibility();
    if (this.disabled) {
      this.insertionForm.disable({ emitEvent: false });
    }
  }

  private initValueChangeHandlers(): void {
    this.insertionForm.controls.socialCareInProgress.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSocialStructureVisibility());
    this.insertionForm.controls.aseService.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateAseServiceLyonVisibility());
    this.insertionForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
  }

  private updateSocialStructureVisibility(): void {
    if (this.insertionForm.controls.socialCareInProgress.value === true) {
      this.insertionForm.controls.socialStructureName.enable();
    } else {
      this.insertionForm.controls.socialStructureName.reset();
      this.insertionForm.controls.socialStructureName.disable();
    }
  }

  private updateAseServiceLyonVisibility(): void {
    if (this.insertionForm.controls.aseService.value === true) {
      this.insertionForm.controls.aseServiceLyon.enable();
    } else {
      this.insertionForm.controls.aseServiceLyon.reset();
      this.insertionForm.controls.aseServiceLyon.disable();
    }
  }
}
